<template>
  <div
    class="flex flex-col min-h-0 bg-white dark:bg-[var(--bg-card)] font-poppins"
    :class="embedded ? 'h-[560px] rounded-2xl border border-gray-200 dark:border-white/10 overflow-hidden' : 'h-full'"
  >
    <!-- Header -->
    <div class="flex items-center gap-3 px-4 py-3 bg-[var(--color-primary)] text-white shrink-0">
      <div class="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center shrink-0">
        <MessagesSquare class="w-5 h-5" />
      </div>
      <div class="flex-1 min-w-0">
        <p class="font-semibold leading-tight">Napíšte nám</p>
        <p class="text-xs text-white/80 truncate">{{ subtitle }}</p>
      </div>
      <button
        v-if="chat.view === 'thread'"
        type="button"
        class="text-xs font-medium px-2.5 py-1.5 rounded-lg bg-white/15 hover:bg-white/25 transition-colors"
        @click="chat.close()"
      >
        Ukončiť chat
      </button>
      <slot name="header-actions" />
    </div>

    <!-- Loading a remembered chat -->
    <div v-if="chat.isLoading && !chat.conversation" class="flex-1 flex items-center justify-center">
      <Loader2 class="w-6 h-6 animate-spin text-gray-400" />
    </div>

    <!-- Start form -->
    <form
      v-else-if="chat.view === 'start'"
      class="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-4"
      @submit.prevent="submitStart"
    >
      <p class="text-sm text-gray-600 dark:text-gray-300">
        Máte otázku k produktu alebo objednávke? Napíšte nám, odpovieme čo najskôr.
      </p>

      <div v-if="authStore.isAuthenticated" class="text-sm rounded-xl bg-gray-50 dark:bg-white/5 px-3 py-2 text-gray-700 dark:text-gray-200">
        Píšete ako <strong>{{ authStore.displayName }}</strong>
      </div>

      <template v-else>
        <div>
          <label :class="labelClass" for="chat-name">Meno a priezvisko *</label>
          <input id="chat-name" v-model="chat.draft.name" type="text" autocomplete="name" :class="fieldClass('name')" />
          <p v-if="errors.name" :class="errorClass">{{ errors.name[0] }}</p>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3" :class="{ 'sm:grid-cols-1': !embedded }">
          <div>
            <label :class="labelClass" for="chat-phone">Telefón *</label>
            <input id="chat-phone" v-model="chat.draft.phone" type="tel" autocomplete="tel" placeholder="+421 9xx xxx xxx" :class="fieldClass('phone')" />
            <p v-if="errors.phone" :class="errorClass">{{ errors.phone[0] }}</p>
          </div>
          <div>
            <label :class="labelClass" for="chat-email">E-mail <span class="text-gray-400 font-normal">(nepovinný)</span></label>
            <input id="chat-email" v-model="chat.draft.email" type="email" autocomplete="email" :class="fieldClass('email')" />
            <p v-if="errors.email" :class="errorClass">{{ errors.email[0] }}</p>
          </div>
        </div>
        <p class="text-xs text-gray-500 dark:text-gray-400">
          Máte účet?
          <RouterLink :to="{ name: 'auth.login', query: { redirect: $route.fullPath } }" class="font-medium text-[var(--color-primary)] hover:underline">Prihláste sa</RouterLink>
          alebo
          <RouterLink :to="{ name: 'auth.register', query: { redirect: $route.fullPath } }" class="font-medium text-[var(--color-primary)] hover:underline">sa zaregistrujte</RouterLink>
          a konverzácie uvidíte v svojom účte.
        </p>
      </template>

      <!-- Topic -->
      <div>
        <p :class="labelClass">Téma</p>
        <div class="flex flex-wrap gap-2">
          <button
            v-for="option in topics"
            :key="option.value"
            type="button"
            class="px-3 py-1.5 rounded-full text-sm border transition-colors"
            :class="chat.draft.topic === option.value
              ? 'bg-[var(--color-primary)] border-[var(--color-primary)] text-white'
              : 'border-gray-300 text-gray-700 hover:border-[var(--color-primary)] dark:border-white/15 dark:text-gray-200'"
            @click="chat.draft.topic = option.value"
          >
            {{ option.label }}
          </button>
        </div>
      </div>

      <!-- Product: the picked one, or a search -->
      <div v-if="chat.draft.topic === 'product'">
        <label :class="labelClass" for="chat-product">Produkt</label>
        <div
          v-if="chat.draft.product"
          class="flex items-center gap-3 rounded-xl border border-gray-200 dark:border-white/10 p-2 pr-3"
        >
          <img v-if="chat.draft.product.main_image" :src="chat.draft.product.main_image" alt="" class="w-12 h-12 rounded-lg object-cover bg-gray-100 shrink-0" />
          <span v-else class="w-12 h-12 rounded-lg bg-gray-100 dark:bg-white/10 flex items-center justify-center shrink-0">
            <Package class="w-5 h-5 text-gray-400" />
          </span>
          <div class="flex-1 min-w-0">
            <p class="text-sm font-medium text-gray-900 dark:text-gray-100 line-clamp-2 leading-snug">{{ chat.draft.product.name }}</p>
            <p v-if="chat.draft.product.variant_name" class="text-xs text-gray-500 dark:text-gray-400">{{ chat.draft.product.variant_name }}</p>
          </div>
          <button type="button" class="shrink-0 text-xs font-medium text-[var(--color-primary)] hover:underline" @click="changeProduct">
            Zmeniť
          </button>
        </div>
        <template v-else>
          <div class="relative">
            <Search class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <input
              id="chat-product"
              ref="productInput"
              v-model="productQuery"
              type="search"
              autocomplete="off"
              placeholder="Hľadajte podľa názvu…"
              :class="[fieldClass('product_id'), 'pl-9']"
              @input="searchProducts"
            />
          </div>
          <div v-if="productQuery.trim().length >= 2" class="mt-2 rounded-xl border border-gray-200 dark:border-white/10 divide-y divide-gray-100 dark:divide-white/5 overflow-hidden">
            <p v-if="productsLoading && !productResults.length" class="px-3 py-2.5 text-sm text-gray-400">Hľadám…</p>
            <p v-else-if="!productResults.length" class="px-3 py-2.5 text-sm text-gray-500">Nič sme nenašli</p>
            <button
              v-for="item in productResults"
              :key="item.id"
              type="button"
              class="w-full flex items-center gap-3 px-2 py-2 text-left hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
              @click="pickProduct(item)"
            >
              <img v-if="item.main_image" :src="item.main_image" alt="" class="w-10 h-10 rounded-lg object-cover bg-gray-100 shrink-0" />
              <span v-else class="w-10 h-10 rounded-lg bg-gray-100 dark:bg-white/10 shrink-0"></span>
              <span class="flex-1 min-w-0 text-sm text-gray-900 dark:text-gray-100 line-clamp-2 leading-snug">{{ item.name }}</span>
              <span class="shrink-0 text-xs font-medium text-gray-500 dark:text-gray-400">{{ formatPrice(item.price) }}</span>
            </button>
          </div>
        </template>
        <p v-if="errors.product_id" :class="errorClass">{{ errors.product_id[0] }}</p>
      </div>

      <!-- Order -->
      <div v-if="chat.draft.topic === 'order'">
        <label :class="labelClass" for="chat-order">Objednávka</label>
        <template v-if="authStore.isAuthenticated">
          <div v-if="ordersLoading" class="text-sm text-gray-400 py-2">Načítavam objednávky…</div>
          <select v-else-if="orders.length" id="chat-order" v-model="chat.draft.order_number" :class="fieldClass('order_number')">
            <option value="">Vyberte objednávku</option>
            <option v-for="order in orders" :key="order.order_number" :value="order.order_number">
              #{{ order.order_number }} · {{ formatDate(order.created_at) }} · {{ formatPrice(order.total) }}
            </option>
          </select>
          <p v-else class="text-sm text-gray-500 py-1">Zatiaľ nemáte žiadne objednávky.</p>
        </template>
        <template v-else>
          <input id="chat-order" v-model="chat.draft.order_number" type="text" placeholder="Číslo objednávky" :class="fieldClass('order_number')" />
          <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">Nájdete ho v potvrdzujúcom e-maile.</p>
        </template>
      </div>

      <div>
        <label :class="labelClass" for="chat-message">Správa *</label>
        <textarea id="chat-message" v-model="chat.draft.message" rows="3" maxlength="2000" :class="[fieldClass('message'), 'resize-none']" />
        <p v-if="errors.message" :class="errorClass">{{ errors.message[0] }}</p>
      </div>

      <p v-if="chat.error && !Object.keys(errors).length" :class="errorClass">{{ chat.error }}</p>

      <button
        type="submit"
        :disabled="chat.isSending"
        class="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[var(--color-primary)] hover:bg-[var(--color-primary-dark)] text-white text-sm font-semibold disabled:opacity-50 transition-colors"
      >
        <Loader2 v-if="chat.isSending" class="w-4 h-4 animate-spin" />
        Začať chat
      </button>
    </form>

    <!-- Conversation (open or ended) -->
    <ChatThread
      v-else
      class="flex-1"
      :messages="chat.messages"
      perspective="customer"
      :sending="chat.isSending"
      :placeholder="chat.isClosed ? 'Napíšte správu a konverzácia sa znovu otvorí…' : 'Napíšte správu…'"
      @send="send"
    >
      <template #notice>
        <!-- "Opýtať sa" on a product page while this chat is still open -->
        <div v-if="chat.productPrompt && chat.view === 'thread'" class="border-t border-gray-200 dark:border-white/10 px-4 py-3 bg-gray-50 dark:bg-white/5">
          <p class="text-sm text-gray-700 dark:text-gray-200">
            Chcete sa spýtať na <strong>{{ chat.productPrompt.name }}</strong>?
          </p>
          <div class="mt-2 flex gap-2">
            <button
              type="button"
              class="inline-flex items-center px-3 py-1.5 rounded-lg bg-[var(--color-primary)] hover:bg-[var(--color-primary-dark)] text-white text-xs font-semibold transition-colors"
              @click="chat.acceptProductPrompt()"
            >
              Nová otázka k produktu
            </button>
            <button
              type="button"
              class="px-3 py-1.5 rounded-lg text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-white/10"
              @click="chat.productPrompt = null"
            >
              Pokračovať v tomto chate
            </button>
          </div>
        </div>
        <div v-if="chat.view === 'ended'" class="border-t border-gray-200 dark:border-white/10 px-4 py-3 space-y-3 bg-gray-50 dark:bg-white/5">
          <p class="text-sm font-semibold text-gray-900 dark:text-gray-100">Konverzácia bola ukončená</p>

          <!-- Guest: keep the chat by registering, or get a transcript -->
          <template v-if="chat.conversation?.is_guest">
            <RouterLink
              :to="registerLink"
              class="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-[var(--color-primary)] hover:bg-[var(--color-primary-dark)] text-white text-sm font-semibold transition-colors"
            >
              <UserPlus class="w-4 h-4" />
              Zaregistrovať sa a uchovať konverzáciu
            </RouterLink>

            <form v-if="!transcriptMessage" class="flex gap-2" @submit.prevent="sendTranscript">
              <input v-model="transcriptEmail" type="email" required placeholder="Váš e-mail" :class="[fieldClass('email'), 'flex-1']" />
              <button
                type="submit"
                :disabled="transcriptSending"
                class="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-300 dark:border-white/15 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-white dark:hover:bg-white/10 disabled:opacity-50"
              >
                <Mail class="w-4 h-4" />
                Poslať prepis
              </button>
            </form>
            <p v-else class="text-sm text-green-700 dark:text-green-400">{{ transcriptMessage }}</p>
            <p v-if="errors.email" :class="errorClass">{{ errors.email[0] }}</p>
          </template>

          <RouterLink
            v-else
            :to="{ name: 'user.messages', params: { uuid: chat.conversation?.uuid } }"
            class="block text-sm font-medium text-[var(--color-primary)] hover:underline"
          >
            Konverzáciu nájdete v časti Môj účet → Správy
          </RouterLink>

          <button type="button" class="text-sm font-medium text-gray-600 dark:text-gray-300 hover:underline" @click="chat.startNew()">
            Začať novú konverzáciu
          </button>
        </div>
        <p v-else-if="chat.error" :class="[errorClass, 'px-4 pb-1']">{{ chat.error }}</p>
      </template>
    </ChatThread>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { debounce } from 'lodash-es'
import { MessagesSquare, Loader2, UserPlus, Mail, Package, Search } from 'lucide-vue-next'
import { useAuthStore } from '@/stores/auth'
import { useChatStore } from '@/stores/chat'
import { ordersAPI, productsAPI } from '@/api'
import { hasFeature } from '@/brand'
import ChatThread from './ChatThread.vue'

defineProps({
  // on the contact page: a bordered card of fixed height instead of filling the widget
  embedded: { type: Boolean, default: false },
})

const authStore = useAuthStore()
const chat = useChatStore()

const topics = [
  { value: 'support', label: 'Všeobecná podpora' },
  ...(hasFeature('chatProducts') ? [{ value: 'product', label: 'Spýtať sa k produktu' }] : []),
  { value: 'order', label: 'Objednávka' },
]

const productQuery = ref('')
const productResults = ref([])
const productsLoading = ref(false)
const productInput = ref(null)
let productSearchId = 0

const orders = ref([])
const ordersLoading = ref(false)
const transcriptEmail = ref('')
const transcriptSending = ref(false)
const transcriptMessage = ref('')

const labelClass = 'block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1'
const inputClass = 'w-full px-3 py-2 text-sm rounded-xl border bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)] dark:bg-white/5 dark:text-gray-100'
const errorClass = 'text-xs text-red-600 mt-1'
const fieldClass = (field) => [inputClass, errors.value[field] ? 'border-red-500' : 'border-gray-300 dark:border-white/10']

const errors = computed(() => chat.fieldErrors || {})

const subtitle = computed(() => {
  const conversation = chat.conversation
  if (chat.view === 'start') return 'Zvyčajne odpovedáme do niekoľkých minút'
  if (conversation?.topic === 'product') return conversation.product?.name || 'Otázka k produktu'
  return conversation?.order_number ? `Objednávka #${conversation.order_number}` : 'Všeobecná podpora'
})

// newest request wins, older answers arriving late are dropped
const runProductSearch = debounce(async (query) => {
  const id = ++productSearchId
  try {
    const response = await productsAPI.searchProducts({ q: query, per_page: 6 })
    if (id === productSearchId) productResults.value = response.data || []
  } catch (err) {
    if (id === productSearchId) productResults.value = []
  } finally {
    if (id === productSearchId) productsLoading.value = false
  }
}, 250)

const searchProducts = () => {
  const query = productQuery.value.trim()
  if (query.length < 2) {
    productSearchId++
    productResults.value = []
    productsLoading.value = false
    return
  }
  productsLoading.value = true
  runProductSearch(query)
}

const pickProduct = (item) => {
  chat.draft.product = {
    id: item.id,
    name: item.name,
    variant_name: item.variant_name || null,
    main_image: item.main_image || null,
  }
  productQuery.value = ''
  productResults.value = []
}

const changeProduct = async () => {
  chat.draft.product = null
  await nextTick()
  productInput.value?.focus()
}

const registerLink = computed(() => {
  const conversation = chat.conversation || {}
  const [firstName, ...rest] = (conversation.contact_name || '').trim().split(/\s+/)
  return {
    name: 'auth.register',
    query: {
      redirect: '/user/messages',
      first_name: firstName || undefined,
      last_name: rest.join(' ') || undefined,
      email: conversation.contact_email || undefined,
      phone: conversation.contact_phone || undefined,
    },
  }
})

const formatDate = (value) => value ? new Date(value).toLocaleDateString('sk-SK') : ''
const formatPrice = (value) => `${Number(value || 0).toFixed(2).replace('.', ',')} €`

const loadOrders = async () => {
  if (!authStore.isAuthenticated || orders.value.length || ordersLoading.value) return
  ordersLoading.value = true
  try {
    const response = await ordersAPI.getOrders({ per_page: 30 })
    orders.value = response.data || []
  } catch (err) {
    orders.value = []
  } finally {
    ordersLoading.value = false
  }
}

const submitStart = () => chat.start()

const send = async (body, done) => {
  if (await chat.send(body)) done()
}

const sendTranscript = async () => {
  transcriptSending.value = true
  const message = await chat.sendTranscript(transcriptEmail.value)
  transcriptSending.value = false
  if (message) transcriptMessage.value = message
}

watch(() => chat.draft.topic, (topic) => {
  if (topic === 'order') loadOrders()
})

watch(() => chat.conversation?.uuid, () => {
  transcriptMessage.value = ''
  transcriptEmail.value = chat.conversation?.contact_email || ''
}, { immediate: true })

watch(() => authStore.isAuthenticated, () => {
  orders.value = []
  if (chat.draft.topic === 'order') loadOrders()
})

onMounted(() => {
  chat.attach()
  if (chat.draft.topic === 'order') loadOrders()
})
onUnmounted(() => chat.detach())
</script>
